
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface ExportButtonProps {
  onExportCSV: () => Promise<boolean> | boolean;
  onExportExcel?: () => Promise<boolean> | boolean;
  onExportPDF?: () => Promise<boolean> | boolean;
  className?: string;
  disabled?: boolean;
  label?: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  onExportCSV,
  onExportExcel,
  onExportPDF,
  className = '',
  disabled = false,
  label = 'Export'
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (
    exportFn: (() => Promise<boolean> | boolean) | undefined,
    format: string
  ) => {
    if (!exportFn) {
      toast.error(`Export to ${format} not supported yet`);
      return;
    }

    setIsExporting(true);
    try {
      const result = await exportFn();
      if (result) {
        toast.success(`Successfully exported to ${format}`);
      } else {
        toast.error(`Failed to export to ${format}`);
      }
    } catch (error) {
      console.error(`Error during ${format} export:`, error);
      toast.error(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={className}
          disabled={disabled || isExporting}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem 
          onClick={() => handleExport(onExportCSV, 'CSV')}
          disabled={isExporting || !onExportCSV}
        >
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExport(onExportExcel, 'Excel')}
          disabled={isExporting || !onExportExcel}
        >
          Export as Excel
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExport(onExportPDF, 'PDF')}
          disabled={isExporting || !onExportPDF}
        >
          Export as PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton;
